import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import Page from '../../../components/Page';
import PageLayout from '../../../components/PageLayout/PageLayout';
import PageTitle from '../../../components/PageLayout/PageTitle';
import * as paths from '../../../routes/paths';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserProfile } from '../../../selectors/user';
import {
  useLibrary,
  useOnboardingStatus,
  useRenameContent,
} from '../../../hooks';
import Spinner from '@raydiant/raydial/components/Spinner';
import LibraryPageActionButton from './components/LibraryPageActionButton';
import { useToastState } from '@raydiant/raydial/components/Toast';
import useCreateFolder from '../../../hooks/useCreateFolder';
import { Content, CreateFolder } from '@raydiant/api-client-js';
import {
  getQueryString,
  getSortedLibraryData,
  addQueryParamsToUrl,
  LibraryQueryParams,
  changeV3DomainProfilesToV4,
} from '../utils';
import DomainProfiles from './DomainProfiles';
import { createNewId } from '../../../utilities/identifiers';
import {
  selectIsManageMode,
  selectLayoutOptions,
  selectMainSortOptions,
  selectSelectedNodeIds,
} from '../selectors';
import { selectLocalUploads } from '../../../selectors/fileUploads';
import Card from '@raydiant/raydial/components/Card';
import * as presentationPageActions from '../../PresentationPage/actions';
import { capitalize } from '../../../utilities/stringUtils';
import DeleteContentDialog from './components/DeleteContentDialog';
import CreateFolderDialog from './components/CreateFolderDialog';
import LibraryOnboardingDialog from './components/LibraryOnboardingDialog';
import LibraryPageActionBar from './components/LibraryPageActionBar';
import EmptyLibrary from './components/EmptyLibrary';
import ListViewLayout from './components/ListViewLayout';
import GridViewLayout from './components/GridViewLayout';
import RenameContentDialog from './components/RenameContentDialog';
import ShareDialog from './components/ShareDialog';
import { BACK_BUTTON_ICON } from '../../LocationsPage/DeviceShare/DeviceShare';
import _, { xor } from 'lodash';
import useMoveAllItemsToFolder from '../../../hooks/useMoveAllItemsToFolder';
import {
  canDeleteContent,
  getResourceIdsFromNodeIds,
} from '../../../utilities';
import * as actions from '../actions';
import * as libraryTreeActions from '../../../components/LibraryTree/actions';
import PaperModalV2 from 'raydiant-elements/core/PaperModalV2';
import cn from 'classnames';
import MoveContent from './components/MoveContent';
import Error from '../../../components/Error/Error';
import { selectOtherDomainProfiles } from '../../../selectors/v2/domains';
import useDeleteContent from '../../../hooks/useDeleteContent';
import miraClient from '../../../clients/raydiant';
import LibraryDragLayerV2 from '../../../components/LibraryDragLayer/LibraryDragLayerV2';

export type LibraryPageModalType =
  | 'folder'
  | 'share'
  | 'rename'
  | 'delete'
  | 'move';

const LibraryPageV2 = () => {
  const state = useToastState();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  // Router & URL
  const searchMatch = useRouteMatch({ path: paths.librarySearch.pattern });
  const sharedMatch = useRouteMatch({
    path: paths.librarySharedWithMe.pattern,
  });
  const profilesMatch = useRouteMatch({
    path: paths.libraryUsersLibraries.pattern,
  });

  const queryParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      scope: sharedMatch ? 'shared' : 'owned',
      profile_id: searchParams.get('profile_id') ?? '',
      search: searchParams.get('search') ?? '',
      folder_id: searchParams.get('folder_id') ?? '',
      sort_by: searchParams.get('sort_by') ?? '',
      sort_direction: searchParams.get('sort_direction') ?? '',
      back_to: searchParams.get('back_to') ?? '',
    };
  }, [location.search, sharedMatch]);

  const baseUrl = useMemo(() => {
    if (sharedMatch) return paths.librarySharedWithMe();
    if (profilesMatch) return paths.libraryUsersLibraries();
    if (searchMatch) return paths.librarySearch();
    return paths.library();
  }, [profilesMatch, searchMatch, sharedMatch]);

  const pageUrl = useMemo(() => {
    return addQueryParamsToUrl(baseUrl, queryParams);
  }, [baseUrl, queryParams]);

  const showDomainProfiles = profilesMatch && !queryParams.profile_id;

  // Selectors

  const currentUser = useSelector(selectUserProfile);
  const sortOptions = useSelector(selectMainSortOptions);
  const layoutOptions = useSelector(selectLayoutOptions);
  const batchMode = useSelector(selectIsManageMode);
  const localUploads = useSelector(selectLocalUploads);
  const selectedNodeIds = useSelector(selectSelectedNodeIds);
  const otherDomainProfiles = useSelector(selectOtherDomainProfiles);

  // States

  const [isAddPopOverOpen, setIsAddPopOverOpen] = useState(false);
  const [modalType, setModalType] = useState<LibraryPageModalType | null>(null);
  // Since we are fetching all presentations, playlists, and folders as Contents,
  // there might be duplicate IDs. However, resourceId is always unique.
  const [selectedResourceId, setSelectedResourceId] = useState('');
  const [showMoveContentSidebar, setShowMoveContentSidebar] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Queries

  const { data: onboardingData, isLoading: isOnboardingLoading } =
    useOnboardingStatus();
  const {
    data: libraryData,
    isLoading: libraryLoading,
    refetch,
  } = useLibrary(queryParams, {
    enabled: !showDomainProfiles,
  });
  const { mutateAsync: createFolder, isLoading: isCreateFolderLoading } =
    useCreateFolder(queryParams);
  const { mutateAsync: renameContent, isLoading: isRenameContentLoading } =
    useRenameContent();
  const { mutateAsync: moveAllItemsToFolder } = useMoveAllItemsToFolder();
  const { mutateAsync: deleteContent } = useDeleteContent();

  // Memoizers
  const sortedLibraryData = useMemo(
    () => libraryData && getSortedLibraryData(libraryData, sortOptions),
    [libraryData, sortOptions],
  );

  const v4DomainProfiles = useMemo(
    () => changeV3DomainProfilesToV4(otherDomainProfiles),
    [otherDomainProfiles],
  );

  const sortedDomainUsers = useMemo(() => {
    if (!v4DomainProfiles || !sortOptions || !currentUser)
      return v4DomainProfiles || [];

    const isKeyName = sortOptions.property === 'name';

    return _.orderBy(
      v4DomainProfiles,
      [(item) => item.name?.toLowerCase()],
      [isKeyName ? sortOptions.direction : 'asc'],
    );
  }, [v4DomainProfiles, sortOptions, currentUser]);

  const selectedContent = useMemo(
    () =>
      sortedLibraryData?.data.find(
        (content) => content.resourceId === selectedResourceId,
      ),
    [selectedResourceId, sortedLibraryData?.data],
  );

  const folderPaths = useMemo(
    () => sortedLibraryData?.folderPath || [],
    [sortedLibraryData],
  );

  const disableManageMultiple = useMemo(() => {
    if (!currentUser || !sortedLibraryData) return true;

    if (profilesMatch && !queryParams.profile_id) {
      return true;
    }

    return false;
  }, [currentUser, sortedLibraryData, profilesMatch, queryParams.profile_id]);

  const disableMoveContent = useMemo(() => {
    if (!currentUser || !sortedLibraryData) return true;

    const data = sortedLibraryData?.data || [];
    const filteredData = data.filter((content) =>
      selectedNodeIds.includes(`${content.resourceType}|${content.id}`),
    );

    return filteredData.some((content) => content.ownedBy.id !== currentUser.id)
      ? true
      : false;
  }, [currentUser, sortedLibraryData, selectedNodeIds]);

  const disableBatchDelete = useMemo(() => {
    if (!currentUser || !sortedLibraryData) return true;

    const data = sortedLibraryData?.data || [];
    const filteredData = data.filter((content) =>
      selectedNodeIds.includes(`${content.resourceType}|${content.id}`),
    );

    return filteredData.some(
      (content) => !canDeleteContent(currentUser, content),
    )
      ? true
      : false;
  }, [currentUser, sortedLibraryData, selectedNodeIds]);

  const disabledFolderIds = useMemo(() => {
    const selectedFolderIdsFromNodeIds = selectedNodeIds
      .filter((nodeId) => nodeId.includes('folder'))
      .map((nodeId) => nodeId.split('|')[1])
      .filter(Boolean);

    const selectedFolderId =
      selectedContent?.resourceType === 'folder' ? [selectedContent.id] : [];

    return [...selectedFolderIdsFromNodeIds, ...selectedFolderId];
  }, [selectedContent?.id, selectedContent?.resourceType, selectedNodeIds]);

  // Variables
  const isLoading = libraryLoading || isOnboardingLoading;
  const libraryEmpty = sortedLibraryData?.data.length === 0;
  const selectedProfileId = queryParams.profile_id;
  const selectedFolderId = queryParams.folder_id;
  const previousFolderPath = folderPaths[folderPaths.length - 2];
  const previousFolderId = previousFolderPath?.id || '';
  const isLibraryOnboardingCompleted = !!onboardingData?.some(
    (item) => item.name === 'library',
  );

  // Callbacks
  const navigateWithQuery = useCallback(
    (pathname: string, queryParams: LibraryQueryParams) => {
      history.push({
        pathname: pathname,
        search: getQueryString(queryParams),
      });
    },
    [history],
  );

  const setSelectedNodeIds = useCallback(
    (nodeIds: string[]) => {
      dispatch(actions.setSelectedNodeIds(nodeIds));
    },
    [dispatch],
  );

  const toggleNodeId = useCallback(
    (content: Content) => {
      const nodeId = `${content.resourceType}|${content.id}`;
      setSelectedNodeIds(xor(selectedNodeIds, [nodeId]));
    },
    [setSelectedNodeIds, selectedNodeIds],
  );

  const onToggleAddPopOver = useCallback((open: boolean) => {
    setIsAddPopOverOpen(open);
  }, []);

  const onAddPopOverAction = useCallback(
    (type: LibraryPageModalType) => {
      onToggleAddPopOver(false);
      setModalType(type);
    },
    [onToggleAddPopOver],
  );

  const onCloseModal = useCallback(() => {
    onToggleAddPopOver(false);
    setModalType(null);
    setShowMoveContentSidebar(false);
  }, [onToggleAddPopOver]);

  const onNewFolder = useCallback(
    async (folderName: string) => {
      try {
        const payload: CreateFolder = {
          name: folderName,
          description: null,
          type: 'library',
        };

        const folder = await createFolder(payload);

        onCloseModal();

        state.add({
          title: 'Folder Added',
          description: 'New folder added successfully',
        });

        const updatedQuery = {
          ...queryParams,
          folder_id: folder.id,
        };

        navigateWithQuery(baseUrl, updatedQuery);
      } catch (error) {
        state.add({
          title: 'Something went wrong',
          description: 'Please try again',
          variant: 'caution',
        });
      }
    },
    [
      baseUrl,
      createFolder,
      navigateWithQuery,
      onCloseModal,
      queryParams,
      state,
    ],
  );

  const onNewPlaylist = useCallback(() => {
    history.push(
      paths.newPlaylist({
        playlistId: createNewId(),
        folderId: selectedFolderId,
        // We don't want to come back to the app selector page,
        // forward the backTo to the next page.
        backTo: pageUrl,
        saveTo: pageUrl,
        backToLabel: 'Back to Library',
      }),
    );
  }, [history, pageUrl, selectedFolderId]);

  const onNewPresentation = useCallback(() => {
    const targetPath = paths.applications({
      backTo: pageUrl,
      folderId: selectedFolderId,
    });

    history.push(targetPath);
  }, [history, pageUrl, selectedFolderId]);

  const onPresentationCardClick = useCallback(
    (applicationId: string) => {
      dispatch(presentationPageActions.clearUnsavedPresentation(applicationId));

      history.push(
        paths.newPresentation({
          applicationId,
          folderId: queryParams.folder_id,
          backTo: pageUrl,
          saveTo: pageUrl,
          backToLabel: 'Back to Library',
        }),
      );
    },
    [dispatch, history, pageUrl, queryParams.folder_id],
  );

  const onBackToParentFolder = useCallback(() => {
    const nextQueryParams = {
      ...queryParams,
    };

    if (selectedFolderId) {
      nextQueryParams.folder_id = previousFolderId;
    } else if (selectedProfileId) {
      nextQueryParams.profile_id = '';
    }

    navigateWithQuery(baseUrl, nextQueryParams);
  }, [
    baseUrl,
    navigateWithQuery,
    previousFolderId,
    queryParams,
    selectedFolderId,
    selectedProfileId,
  ]);

  const onSelectUser = useCallback(
    (profileId: string) => {
      const nextQueryParams = {
        ...queryParams,
        profile_id: profileId,
      };
      navigateWithQuery(baseUrl, nextQueryParams);
    },
    [baseUrl, navigateWithQuery, queryParams],
  );

  const onOpenFolder = useCallback(
    (folderId: string) => {
      const nextQueryParams = {
        ...queryParams,
        folder_id: folderId,
      };
      navigateWithQuery(baseUrl, nextQueryParams);
    },
    [baseUrl, navigateWithQuery, queryParams],
  );

  const onEditPlaylist = useCallback(
    (playlistId: string) => {
      const basePath = `${paths.library()}?${getQueryString(queryParams)}`;
      history.push(
        paths.editPlaylist(playlistId, {
          backTo: basePath,
          backToLabel: 'Back To Library',
          saveTo: basePath,
          folderId: queryParams.folder_id,
        }),
      );
    },
    [history, queryParams],
  );

  const onEditPresentation = useCallback(
    (presentationId: string) => {
      dispatch(
        presentationPageActions.clearUnsavedPresentation(presentationId),
      );
      history.push(
        paths.editPresentation(presentationId, {
          backTo: pageUrl,
          backToLabel: 'Back to Library',
          saveTo: pageUrl,
        }),
      );
    },
    [pageUrl, dispatch, history],
  );

  const onSelectContent = useCallback(
    (content: Content) => {
      if (batchMode) {
        return toggleNodeId(content);
      }

      switch (content.resourceType) {
        case 'folder':
          return onOpenFolder(content.id);
        case 'playlist':
          return onEditPlaylist(content.id);
        case 'presentation':
          return onEditPresentation(content.id);
        default:
          return;
      }
    },
    [batchMode, onEditPlaylist, onEditPresentation, onOpenFolder, toggleNodeId],
  );

  const onContentCardMoreActions = useCallback((resourceId: string) => {
    setSelectedResourceId(resourceId);
  }, []);

  const onContentCardAction = useCallback((type: LibraryPageModalType) => {
    if (type === 'move') {
      setShowMoveContentSidebar(true);
    } else {
      setModalType(type);
    }
  }, []);

  const onMoveNodeIdsToFolder = useCallback(
    async (nodeIds: string[], folderId: string | null) => {
      const { presentationIds, playlistIds, folderIds } =
        getResourceIdsFromNodeIds(nodeIds);

      try {
        await moveAllItemsToFolder({
          presentationIds,
          playlistIds,
          folderIds,
          parentFolderId: folderId || null,
        });

        onCloseModal();

        dispatch(actions.clearSelectedNodeIds());
        dispatch(libraryTreeActions.clearSelectedNodeIds());

        state.add({
          title: 'Contents Moved',
          description: `${selectedNodeIds.length} contents moved successfully`,
        });
      } catch (error) {
        state.add({
          title: 'Something went wrong',
          description: 'Please try again',
          variant: 'caution',
        });
      }
    },
    [selectedNodeIds, moveAllItemsToFolder, onCloseModal, dispatch, state],
  );

  const onMoveBatchContent = useCallback(
    (folderId: string | null) => {
      onMoveNodeIdsToFolder(selectedNodeIds, folderId);
    },
    [onMoveNodeIdsToFolder, selectedNodeIds],
  );

  const onDeleteNodeIds = useCallback(async () => {
    setIsDeleting(true);

    const { presentationIds, playlistIds, folderIds } =
      getResourceIdsFromNodeIds(selectedNodeIds);

    try {
      await Promise.all([
        ...presentationIds.map((id) =>
          deleteContent({ id, resourceType: 'presentation' }),
        ),
        ...playlistIds.map((id) =>
          deleteContent({ id, resourceType: 'playlist' }),
        ),
        ...folderIds.map((id) => deleteContent({ id, resourceType: 'folder' })),
      ]);

      onCloseModal();

      dispatch(actions.clearSelectedNodeIds());
      dispatch(libraryTreeActions.clearSelectedNodeIds());

      state.add({
        title: 'Contents Deleted',
        description: `${selectedNodeIds.length} contents deleted successfully`,
      });
    } catch (error) {
      state.add({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'caution',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [selectedNodeIds, onCloseModal, dispatch, state, deleteContent]);

  const onRenameContent = useCallback(
    async (name: string) => {
      if (!selectedContent) return;

      try {
        await renameContent({
          id: selectedContent.id,
          params: { name, resourceType: selectedContent.resourceType },
        });

        onCloseModal();

        state.add({
          title: `${capitalize(selectedContent.resourceType)} renamed`,
          description: `${capitalize(
            selectedContent.resourceType,
          )} renamed successfully`,
        });
      } catch (error) {
        state.add({
          title: 'Something went wrong',
          description: 'Please try again',
          variant: 'caution',
        });
      }
    },
    [selectedContent, renameContent, onCloseModal, state],
  );

  const onMoveContent = useCallback(
    async (folderId: string | null) => {
      if (!selectedContent) return;

      const resourceType = selectedContent?.resourceType;

      try {
        await moveAllItemsToFolder({
          presentationIds:
            resourceType === 'presentation' ? [selectedContent.id] : [],
          playlistIds: resourceType === 'playlist' ? [selectedContent.id] : [],
          folderIds: resourceType === 'folder' ? [selectedContent.id] : [],
          parentFolderId: folderId || null,
        });

        onCloseModal();
        setSelectedResourceId('');

        state.add({
          title: 'Content Moved',
          description: `${selectedContent.name} moved successfully`,
        });
      } catch (error) {
        state.add({
          title: 'Something went wrong',
          description: 'Please try again',
          variant: 'caution',
        });
      }
    },
    [selectedContent, moveAllItemsToFolder, onCloseModal, state],
  );

  const fetchPresentations = useCallback(async (ids: string[]) => {
    try {
      const presentations = await miraClient.getPresentations({ ids });
      return presentations;
    } catch (error) {
      console.error(error);
    }
  }, []);

  // Effects
  useEffect(() => {
    // Reset batchmode when we switch between pages to prevent items getting multiselected between pages
    // Currently My Library, Shared with Me, User Libraries are same page so we need to make sure it is cleared
    dispatch(actions.setManageMode(false));
    dispatch(actions.clearSelectedNodeIds());
    dispatch(libraryTreeActions.clearSelectedNodeIds());
  }, [dispatch, baseUrl, selectedProfileId]);

  useEffect(() => {
    const hasUploadingItems = Object.values(localUploads).some(
      (item) => item.isUploading,
    );

    if (hasUploadingItems) {
      refetch();
    }
  }, [localUploads, refetch]);

  useEffect(() => {
    if (!libraryData) return;

    let uploadingPresentationIds = libraryData?.data
      .filter(
        (content) =>
          content.resourceType === 'presentation' &&
          content.presentationData?.fileUploads?.some(
            (fileUpload) =>
              !fileUpload?.completedSteps?.some(
                (step) => step.stepName === 'upload_completed',
              ),
          ),
      )
      .map((content) => content.id);

    if (uploadingPresentationIds.length === 0) {
      refetch();
      return;
    }

    let isFetching = false;

    const interval = setInterval(async () => {
      if (isFetching || uploadingPresentationIds.length === 0) return;
      isFetching = true;

      const updatedPresentations = await fetchPresentations(
        uploadingPresentationIds,
      );

      uploadingPresentationIds = uploadingPresentationIds.filter((id) => {
        const presentation = updatedPresentations?.find((p) => p.id === id);
        const isStillUploading = presentation?.fileUploads?.some(
          (fileUpload) =>
            !fileUpload?.completedSteps?.some(
              (step) => step.stepName === 'upload_completed',
            ),
        );
        return isStillUploading;
      });

      isFetching = false;

      if (uploadingPresentationIds.length === 0) {
        refetch();
        clearInterval(interval);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [libraryData, fetchPresentations, refetch]);

  // Renders

  const pageTitle = useMemo(() => {
    const showBackButton = selectedProfileId || selectedFolderId;

    let title: string = 'My Library';
    if (sharedMatch) {
      title = 'Shared With Me';
    }
    if (searchMatch) {
      title = 'Search Results';
    }
    if (profilesMatch) {
      if (selectedProfileId) {
        const selectedProfile = sortedDomainUsers.find(
          (profile) => profile.id === selectedProfileId,
        );
        title = `${selectedProfile?.name}'s Library`;
      }
      title = 'User Libraries';
    }
    if (selectedFolderId) {
      title = folderPaths[folderPaths.length - 1]?.name || '';
    }

    return (
      <div className="flex items-center mb-4">
        {showBackButton && (
          <button className="p-2" onClick={onBackToParentFolder}>
            {BACK_BUTTON_ICON}
          </button>
        )}
        <div className="text-2xl font-bold text-black">{title}</div>
      </div>
    );
  }, [
    selectedProfileId,
    selectedFolderId,
    sharedMatch,
    searchMatch,
    profilesMatch,
    onBackToParentFolder,
    sortedDomainUsers,
    folderPaths,
  ]);

  const pageContent: ReactNode = useMemo(() => {
    if (showDomainProfiles) {
      return (
        <DomainProfiles
          layout={layoutOptions}
          domainProfiles={sortedDomainUsers || []}
          onClick={onSelectUser}
        />
      );
    }

    if (queryParams.search && libraryEmpty) {
      return (
        <div className="w-full md:my-20">
          <Error
            title="No results"
            message="There’s no data for the selected filters, please try different search parameter"
          />
        </div>
      );
    }

    if (libraryEmpty) {
      return (
        <EmptyLibrary
          onPresentationCardClick={onPresentationCardClick}
          onNewPresentation={onNewPresentation}
        />
      );
    }

    if (layoutOptions === 'list') {
      return (
        <ListViewLayout
          contents={sortedLibraryData?.data || []}
          batchMode={batchMode}
          selectedNodeIds={selectedNodeIds}
          onClick={onSelectContent}
          onButtonGroupClick={onContentCardMoreActions}
          onButtonGroupItemClick={onContentCardAction}
          onClose={onCloseModal}
        />
      );
    }

    return (
      <GridViewLayout
        contents={sortedLibraryData?.data || []}
        showOwner={baseUrl !== paths.library()}
        batchMode={batchMode}
        selectedNodeIds={selectedNodeIds}
        onClick={onSelectContent}
        onButtonGroupClick={onContentCardMoreActions}
        onButtonGroupItemClick={onContentCardAction}
        onClose={onCloseModal}
        isMoveable={queryParams.scope === 'owned' && !queryParams.profile_id}
        folderId={selectedFolderId}
        onMove={onMoveNodeIdsToFolder}
        onSelectNodeIds={setSelectedNodeIds}
        onToggleNodeId={toggleNodeId}
      />
    );
  }, [
    baseUrl,
    batchMode,
    layoutOptions,
    libraryEmpty,
    onCloseModal,
    onContentCardAction,
    onContentCardMoreActions,
    onMoveNodeIdsToFolder,
    onNewPresentation,
    onPresentationCardClick,
    onSelectContent,
    onSelectUser,
    queryParams.profile_id,
    queryParams.scope,
    queryParams.search,
    selectedFolderId,
    selectedNodeIds,
    setSelectedNodeIds,
    showDomainProfiles,
    sortedDomainUsers,
    sortedLibraryData?.data,
    toggleNodeId,
  ]);

  const pageModal: ReactNode = useMemo(() => {
    if (!selectedContent && modalType !== 'folder') return null;

    switch (modalType) {
      case 'folder':
        return (
          <CreateFolderDialog
            isLoading={isCreateFolderLoading}
            isOpen={true}
            onSubmit={onNewFolder}
            onClose={onCloseModal}
          />
        );
      case 'share':
        if (!selectedContent) return null;
        return (
          <ShareDialog
            content={selectedContent}
            isOpen={true}
            onClose={onCloseModal}
          />
        );
      case 'rename':
        if (!selectedContent) return null;
        return (
          <RenameContentDialog
            content={selectedContent}
            isLoading={isRenameContentLoading}
            isOpen={true}
            onSubmit={onRenameContent}
            onClose={onCloseModal}
          />
        );
      case 'delete':
        if (!selectedContent) return null;
        return (
          <DeleteContentDialog
            content={selectedContent}
            isOpen={true}
            onSubmit={onCloseModal}
            onClose={onCloseModal}
          />
        );
      default:
        return null;
    }
  }, [
    selectedContent,
    modalType,
    isCreateFolderLoading,
    onNewFolder,
    onCloseModal,
    isRenameContentLoading,
    onRenameContent,
  ]);

  if (isLoading) {
    return (
      <Page title="Library">
        <PageLayout
          title={
            <PageTitle
              className="flex justify-between align-top"
              title="Libraries"
              subTitle="View and manage your library and shared content"
              button={
                <LibraryPageActionButton
                  isOpen={isAddPopOverOpen}
                  onClick={onToggleAddPopOver}
                  isDisabled={true}
                  onFolderClick={() => onAddPopOverAction('folder')}
                  onPresentationClick={() => {
                    setIsAddPopOverOpen(false);
                    onNewPresentation();
                  }}
                  onPlaylistClick={() => {
                    setIsAddPopOverOpen(false);
                    onNewPlaylist();
                  }}
                />
              }
            />
          }
        >
          <div className="mb-1.5">
            <LibraryPageActionBar
              onManageMode={() => {}}
              onMove={() => {}}
              onCreate={() => {}}
              onDelete={() => {}}
            />
          </div>
          <div className="flex min-h-[300px] justify-center items-center md:min-h-[503px]">
            <Spinner size="large" />
          </div>
        </PageLayout>
      </Page>
    );
  }

  return (
    <Page title="Library">
      <PageLayout
        title={
          <PageTitle
            className="flex justify-between align-top"
            title="Libraries"
            subTitle="View and manage your library and shared content"
            button={
              <LibraryPageActionButton
                isOpen={isAddPopOverOpen}
                isDisabled={baseUrl !== paths.library()}
                onClick={onToggleAddPopOver}
                onFolderClick={() => onAddPopOverAction('folder')}
                onPresentationClick={() => {
                  setIsAddPopOverOpen(false);
                  onNewPresentation();
                }}
                onPlaylistClick={() => {
                  setIsAddPopOverOpen(false);
                  onNewPlaylist();
                }}
              />
            }
          />
        }
      >
        <div className="mb-6">
          <LibraryPageActionBar
            onManageMode={() => {}}
            onMove={() => setShowMoveContentSidebar(true)}
            onDelete={onDeleteNodeIds}
            onCreate={() => {}}
            isLoading={isDeleting}
            manageDisabled={disableManageMultiple}
            moveContentDisabled={disableMoveContent}
            deleteContentDisabled={disableBatchDelete}
          />
        </div>
        <Card className="py-7 px-8">
          {pageTitle}
          <div className="flex justify-center gap-4 flex-wrap min-h-[288px] md:h-auto md:justify-start md:gap-6">
            {pageContent}
            <LibraryDragLayerV2 contents={sortedLibraryData?.data || []} />
          </div>
        </Card>
        {!isLibraryOnboardingCompleted && <LibraryOnboardingDialog isOpen />}
        {pageModal}
        {(selectedNodeIds.length > 0 || selectedResourceId) && (
          <PaperModalV2
            className={cn('top-[135px] w-[414px] right-2 bottom-2 bg-blue-700')}
            open={showMoveContentSidebar}
            onClose={() => setShowMoveContentSidebar(false)}
          >
            <MoveContent
              disabledFolderIds={disabledFolderIds}
              onMove={batchMode ? onMoveBatchContent : onMoveContent}
            />
          </PaperModalV2>
        )}
      </PageLayout>
    </Page>
  );
};

export default LibraryPageV2;
